// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import { McMcHeaderContext } from "@mcmc/App/Pages/Header/HeaderContext";
import React, { useContext } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    isUpdatingCart: state.pages.checkoutShipping.isUpdatingCart,
    isUpdatingCustomer: state.pages.checkoutShipping.isUpdatingCustomer,
});

interface OwnProps extends WidgetProps {}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

interface CartLayoutStyles {
    container: InjectableCss;
    leftSection: InjectableCss;
    rightSection: InjectableCss;
}

export const cartLayoutStyles: CartLayoutStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            padding-top: 20px;
            margin: -15px;
            width: auto;
            @media print {
                display: block;
                position: relative;
            }
        `,
    },
    leftSection: {
        css: css`
            flex: 1 1 650px;
            padding: 15px;
            min-width: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex: 1 1 100%;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    rightSection: {
        css: css`
            padding: 15px;
            flex: 0 0 300px;
            min-width: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex: 1 1 100%;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
};

export const StickyDiv = styled.div<InjectableCss & { offset: number }>`
    position: sticky;
    top: ${({ offset }: { offset: number }) => offset}px;
    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                css`
                    position: static;
                `,
                null,
                null,
                null,
            ],
            "max",
        )}
    @media print {
        position: relative;
        top: 0;
        page-break-inside: avoid;
        width: 50%;
    }
    ${injectCss}
`;

const styles = cartLayoutStyles;

const CartLayout = ({ id, isUpdatingCart, isUpdatingCustomer }: Props) => {
    const { mobileHeaderHeight } = useContext(McMcHeaderContext);
    return (
        <LoadingOverlay {...styles.container} loading={isUpdatingCart || isUpdatingCustomer}>
            <StyledWrapper {...styles.leftSection} className="cart-left-section">
                <Zone contentId={id} zoneName="content0" />
            </StyledWrapper>
            <StickyDiv {...styles.rightSection} offset={mobileHeaderHeight}>
                <Zone contentId={id} zoneName="content1" />
            </StickyDiv>
        </LoadingOverlay>
    );
};

const cartLayout: WidgetModule = {
    component: connect(mapStateToProps)(CartLayout),
    definition: {
        group: "Cart",
    },
};

export default cartLayout;
